import React from 'react';
import { motion } from 'framer-motion';
import { 
  IconExternalLink, 
  IconCode, 
  IconMobile, 
  IconGlobe, 
  IconApple, 
  IconBriefcase 
} from './icons';
import { projects } from '../data';

const Projects: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'company':
        return IconBriefcase;
      case 'app':
        return IconMobile;
      case 'web':
        return IconGlobe;
      default:
        return IconCode;
    }
  };

  const getProjectColor = (type: string) => {
    switch (type) {
      case 'company':
        return 'text-blue-600';
      case 'app':
        return 'text-green-600';
      case 'web':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <section id="projects" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Mis <span className="gradient-text">Proyectos</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Empresas y aplicaciones que he creado y desarrollado a lo largo de mi carrera
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="card group cursor-pointer"
            >
              {/* Project Header */}
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gray-50 ${getProjectColor(project.type)}`}>
                  {React.createElement(getProjectIcon(project.type), { size: 24 })}
                </div>
                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-primary-600 transition-colors duration-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconExternalLink size={20} />
                  </a>
                )}
              </div>

              {/* Project Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                {project.title}
              </h3>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                {project.description}
              </p>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2">
                {project.technologies.slice(0, 3).map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full"
                  >
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 3 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    +{project.technologies.length - 3} más
                  </span>
                )}
              </div>

              {/* Project Type Badge */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <span className={`text-sm font-medium ${
                  project.type === 'company' ? 'text-blue-600' :
                  project.type === 'app' ? 'text-green-600' :
                  'text-purple-600'
                }`}>
                  {project.type === 'company' ? 'Empresa' :
                   project.type === 'app' ? 'Aplicación' :
                   'Proyecto Web'}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Projects Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="card">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Otros Proyectos Notables</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800">Apps Móviles</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700">
                    <IconApple className="text-gray-400 mr-2" size={16} />
                    <span className="text-sm">Futboling - App para iPhone</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <IconApple className="text-gray-400 mr-2" size={16} />
                    <span className="text-sm">FIC - App de eventos (Foro Israel-Extremadura)</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <IconApple className="text-gray-400 mr-2" size={16} />
                    <span className="text-sm">UtilEver - Pack web + app + intranet</span>
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800">Proyectos Web</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700">
                    <IconGlobe className="text-gray-400 mr-2" size={16} />
                    <span className="text-sm">Sitios web corporativos responsive</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <IconGlobe className="text-gray-400 mr-2" size={16} />
                    <span className="text-sm">Intranets con manejo de grandes volúmenes de datos</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <IconGlobe className="text-gray-400 mr-2" size={16} />
                    <span className="text-sm">Webservices para apps iOS y Android</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">¿Tienes un proyecto en mente?</h3>
            <p className="text-lg mb-6 opacity-90">
              Me especializo en llevar ideas digitales a la realidad, desde apps móviles hasta sistemas empresariales completos.
            </p>
            <a
              href="#contact"
              className="inline-block bg-white text-primary-600 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              Hablemos de tu proyecto
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;